/**
 * Microsoft Graph, called as the SIGNED-IN admin — never as the app.
 *
 * The app holds no application permission and no secret. Every call here carries the
 * admin's own delegated token, so Graph applies the admin's own rights: only an owner
 * of a group can change its members, whatever the app registration has been granted.
 * That is what makes it safe to put membership changes in a browser. See docs/ACCESS.md.
 *
 * Two groups, and only these two. There is no parameter anywhere in this module that
 * accepts a group id from the UI — the ids are constants, and a write to anything else
 * throws before a request is made.
 *
 * FullAccess is ROLE-ASSIGNABLE (isAssignableToRole = true, fixed at creation). Graph
 * lets nobody change a role-assignable group's members without
 * RoleManagement.ReadWrite.Directory AND the Privileged Role Administrator role, which
 * is far more power than this page should ask for. So FullAccess is listed but not
 * editable here; that is `editable: false` below, and addMember/removeMember refuse it.
 */

export const APP_CLIENT_ID = '7424d071-c4ef-423b-9f1c-233b4c5162de'

export type GroupKey = 'full' | 'users'

export interface AccessGroup {
  key: GroupKey
  id: string
  label: string
  /** What membership gives, in one line for the page. */
  gives: string
  editable: boolean
}

export const GROUPS: Record<GroupKey, AccessGroup> = {
  full: {
    key: 'full',
    id: 'fd0d297e-2cdf-4eba-8024-be37912ffffb',
    label: 'Full access',
    gives: 'Sees every employee.',
    editable: false,
  },
  users: {
    key: 'users',
    id: 'e1dfe2f9-4959-4506-9d54-b0764b9c800c',
    label: 'Users',
    gives: 'Sees only the departments granted to them.',
    editable: true,
  },
}

const GROUP_IDS = new Set(Object.values(GROUPS).map((g) => g.id))

/**
 * Delegated scopes. `User.Read` is already consented and is all the owner check needs,
 * so the Access tab can appear (or not) before IT has approved anything else.
 */
export const OWNER_SCOPES = ['User.Read']
export const MANAGE_SCOPES = [
  'GroupMember.ReadWrite.All',
  'User.ReadBasic.All',
  'AuditLog.Read.All',
]

const GRAPH = 'https://graph.microsoft.com/v1.0'

/** Entra's "user or group is not assigned to this application". */
export const NOT_ASSIGNED = 50105

export interface Person {
  id: string
  name: string
  email: string
}

export interface SignInRow {
  userId: string
  userPrincipalName: string
  userDisplayName: string
  createdDateTime: string
  status: { errorCode: number }
}

export interface SignInSummary {
  userId: string
  name: string
  email: string
  signIns: number
  /** Distinct calendar days, Eastern time. */
  days: number
  first: string
  last: string
  failures: number
  /** Refused with 50105 at least once — in neither group. */
  notAssigned: boolean
  /** The last SUCCESSFUL sign-in; `last` includes refusals. */
  lastSuccess: string | null
}

export class GraphError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'GraphError'
  }
}

export type TokenFn = (scopes: string[]) => Promise<string>
export type FetchFn = (url: string, init?: RequestInit) => Promise<Response>

/**
 * True for the token errors that mean "IT has not consented to these scopes yet".
 *
 * AADSTS65001 is the one silent acquisition returns; 90094 and 90099 are what an
 * interactive attempt shows when the tenant requires an admin to approve. MSAL puts
 * the code in `errorCode` (`consent_required`) or only in the message, depending on
 * the path, so both are read.
 */
export function isConsentPending(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false
  const err = e as { errorCode?: unknown; message?: unknown; subError?: unknown }
  if (err.errorCode === 'consent_required' || err.subError === 'consent_required') return true
  const text = `${String(err.errorCode ?? '')} ${String(err.message ?? '')}`
  return /AADSTS(65001|90094|90099)\b/.test(text)
}

/** Guests carry #EXT# in their UPN. User.ReadBasic.All cannot see userType, so this is the test. */
const isGuest = (upn: string) => upn.toUpperCase().includes('#EXT#')

interface GraphUser {
  id: string
  displayName?: string | null
  mail?: string | null
  userPrincipalName?: string | null
}

const toPerson = (u: GraphUser): Person => ({
  id: u.id,
  name: u.displayName ?? '',
  email: u.mail ?? u.userPrincipalName ?? '',
})

export function createGraph(token: TokenFn, fetchImpl: FetchFn = (u, i) => fetch(u, i)) {
  async function call(
    scopes: string[],
    path: string,
    init: RequestInit & { headers?: Record<string, string> } = {},
  ): Promise<Response> {
    const t = await token(scopes)
    const url = path.startsWith('https://') ? path : `${GRAPH}${path}`
    const res = await fetchImpl(url, {
      ...init,
      headers: { Authorization: `Bearer ${t}`, ...(init.headers ?? {}) },
    })
    if (!res.ok) {
      let code = ''
      let message = `${res.status} ${res.statusText}`
      try {
        const body = (await res.json()) as { error?: { code?: string; message?: string } }
        code = body.error?.code ?? ''
        message = body.error?.message ?? message
      } catch {
        // No JSON body; the status line is all there is.
      }
      throw new GraphError(res.status, code, message)
    }
    return res
  }

  /** Every page of a collection, up to `maxPages`. */
  async function all<T>(
    scopes: string[],
    path: string,
    headers?: Record<string, string>,
    maxPages = 20,
  ): Promise<T[]> {
    const out: T[] = []
    let next: string | undefined = path
    for (let i = 0; next && i < maxPages; i++) {
      const res = await call(scopes, next, { headers })
      const body = (await res.json()) as { value: T[]; '@odata.nextLink'?: string }
      out.push(...body.value)
      next = body['@odata.nextLink']
    }
    return out
  }

  function editableGroup(groupId: string): AccessGroup {
    const g = Object.values(GROUPS).find((x) => x.id === groupId)
    if (!g || !GROUP_IDS.has(groupId)) throw new Error('Not one of this app’s access groups')
    if (!g.editable) throw new Error(`${g.label} can’t be changed from this page`)
    return g
  }

  return {
    /**
     * Which of the two access groups the signed-in user owns.
     *
     * Only `User.Read`: with it, owned objects come back with limited detail (type and
     * id), and the id is all this needs.
     */
    async ownedGroups(): Promise<GroupKey[]> {
      const objs = await all<{ id: string }>(OWNER_SCOPES, '/me/ownedObjects?$select=id')
      const ids = new Set(objs.map((o) => o.id))
      return (Object.values(GROUPS) as AccessGroup[])
        .filter((g) => ids.has(g.id))
        .map((g) => g.key)
    },

    async members(key: GroupKey): Promise<Person[]> {
      const users = await all<GraphUser>(
        MANAGE_SCOPES,
        `/groups/${GROUPS[key].id}/members/microsoft.graph.user` +
          `?$select=id,displayName,mail,userPrincipalName&$top=999`,
      )
      return users.map(toPerson).sort((a, b) => a.name.localeCompare(b.name))
    },

    /** People in the tenant matching `q` by name or email. Members only, never guests. */
    async search(q: string): Promise<Person[]> {
      const term = q.trim().replace(/"/g, '')
      if (term.length < 2) return []
      const search = encodeURIComponent(`"displayName:${term}" OR "mail:${term}"`)
      const res = await call(
        MANAGE_SCOPES,
        `/users?$search=${search}&$select=id,displayName,mail,userPrincipalName&$top=15`,
        { headers: { ConsistencyLevel: 'eventual' } },
      )
      const body = (await res.json()) as { value: GraphUser[] }
      return body.value
        .filter((u) => !isGuest(u.userPrincipalName ?? ''))
        .map(toPerson)
    },

    async addMember(groupId: string, userId: string): Promise<void> {
      const g = editableGroup(groupId)
      await call(MANAGE_SCOPES, `/groups/${g.id}/members/$ref`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ '@odata.id': `${GRAPH}/directoryObjects/${userId}` }),
      })
    },

    /**
     * Removes the membership, not the user. The trailing `/$ref` is load-bearing:
     * without it Graph DELETES THE USER OBJECT when the caller has rights to.
     */
    async removeMember(groupId: string, userId: string): Promise<void> {
      const g = editableGroup(groupId)
      await call(MANAGE_SCOPES, `/groups/${g.id}/members/${encodeURIComponent(userId)}/$ref`, {
        method: 'DELETE',
      })
    },

    /** This app's sign-ins since `since`, newest first. Entra keeps 30 days. */
    async signIns(since: Date): Promise<SignInRow[]> {
      const filter = encodeURIComponent(
        `appId eq '${APP_CLIENT_ID}' and createdDateTime ge ${since.toISOString()}`,
      )
      return all<SignInRow>(MANAGE_SCOPES, `/auditLogs/signIns?$filter=${filter}&$top=1000`)
    },
  }
}

export type GraphClient = ReturnType<typeof createGraph>

const ET = 'America/New_York'
const etDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-CA', { timeZone: ET })

/** Per person, one row. Sorted by most recent attempt first. */
export function summarizeSignIns(rows: SignInRow[]): SignInSummary[] {
  const by = new Map<string, { s: SignInSummary; days: Set<string> }>()
  for (const r of rows) {
    const key = r.userId || r.userPrincipalName.toLowerCase()
    let e = by.get(key)
    if (!e) {
      e = {
        s: {
          userId: r.userId,
          name: r.userDisplayName,
          email: r.userPrincipalName,
          signIns: 0,
          days: 0,
          first: r.createdDateTime,
          last: r.createdDateTime,
          failures: 0,
          notAssigned: false,
          lastSuccess: null,
        },
        days: new Set(),
      }
      by.set(key, e)
    }
    const s = e.s
    const ok = r.status.errorCode === 0
    if (ok) {
      s.signIns++
      e.days.add(etDay(r.createdDateTime))
      if (!s.lastSuccess || r.createdDateTime > s.lastSuccess) s.lastSuccess = r.createdDateTime
    } else {
      s.failures++
    }
    if (r.status.errorCode === NOT_ASSIGNED) s.notAssigned = true
    if (r.createdDateTime < s.first) s.first = r.createdDateTime
    if (r.createdDateTime > s.last) s.last = r.createdDateTime
  }
  return [...by.values()]
    .map(({ s, days }) => ({ ...s, days: days.size }))
    .sort((a, b) => b.last.localeCompare(a.last))
}

/** "Sep 23, 2:05 PM" in Eastern time. */
export function formatEt(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    timeZone: ET,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ------------------------------------------------------------------ department scope

export interface DepartmentGrant {
  email: string
  rcmDepartmentGroup: string | null
  reportingDepartment: string | null
  serviceLine: string | null
}

export interface ScopeRow {
  person: Person
  grants: DepartmentGrant[]
  /** A Users-group member with no grant sees an empty app. */
  noGrant: boolean
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()

/** Pairs each Users-group member with their grants, matched by email, case-blind. */
export function departmentScope(members: Person[], grants: DepartmentGrant[]): ScopeRow[] {
  return members.map((person) => {
    const mine = grants.filter((g) => norm(g.email) === norm(person.email))
    return { person, grants: mine, noGrant: mine.length === 0 }
  })
}

/** "HMO · all service lines", the grant as a reader would say it. */
export function describeGrant(g: DepartmentGrant): string {
  const dept = norm(g.rcmDepartmentGroup) === 'all' ? 'All departments' : (g.rcmDepartmentGroup ?? '—')
  const line = norm(g.serviceLine) === 'all' ? 'all service lines' : (g.serviceLine ?? '—')
  return `${dept} · ${line}`
}
